import { Box } from '@mui/material';
import { useState, useEffect } from 'react';
import { useLoader } from '@/contexts/loader/LoaderContext';
import { User, UpdateUserRequest } from '@/models/company';
import { Validator } from '@/utils/errorHandling';
import {
    useGetUser,
    useGetBackofficeUser,
    useGetSharedEmailEmployments
} from '@/services/usersQuery';
import {
    StyledDialog,
    StyledDialogContainer,
    StyledDialogTitle
} from '@/components/consent/StyledDialog';
import { SemiBold, SMRegular } from '@/typography/Typography';
import { t } from 'i18next';
import { InputText } from '@/components/shared/InputText';
import { Alert } from '@/components/shared/Alert';
import {
    PrimaryButtonStyle,
    SecondaryButtonStyle
} from '@/pages/company/common/IntegrationDialog';
import MobileInput from '@/components/shared/MobileInput';
import { EmailChangeWarningDialog } from './EmailChangeWarningDialog';

type UpdateUserDialogProps = {
    userId?: string;
    open: boolean;
    onClose: () => void;
    onSubmit: (request: UpdateUserRequest) => Promise<void>;
    validator: Validator;
    setValidator: React.Dispatch<React.SetStateAction<Validator>>;
    isAdmin?: boolean;
};

export const UpdateUserDialog = (props: UpdateUserDialogProps) => {
    const [firstName, setFirstName] = useState<string>('');
    const [lastName, setLastName] = useState<string>('');
    const [email, setEmail] = useState<string>('');
    const [phoneNumber, setPhoneNumber] = useState<string>('');
    const [user, setUser] = useState<User>();
    const [confirmOpen, setConfirmOpen] = useState<boolean>(false);
    const { setLoading } = useLoader();

    const queryEnabled = props.open && !!props.userId;

    const { data: employeeData, isFetching: isEmployeeFetching } = useGetUser(
        props.userId ?? '',
        !props.isAdmin && queryEnabled
    );
    const { data: backofficeData, isFetching: isBackofficeFetching } =
        useGetBackofficeUser(
            props.userId ?? '',
            !!props.isAdmin && queryEnabled
        );

    // Employments are an employee-only concept; the endpoint 404s for backoffice
    // admins, so only resolve the shared-email set for the employee edit surface.
    const { data: sharedData, isError: isSharedError } =
        useGetSharedEmailEmployments(
            props.userId ?? '',
            !props.isAdmin && queryEnabled
        );
    const shared = sharedData?.data;

    const userData = props.isAdmin ? backofficeData : employeeData;
    const isFetching = props.isAdmin
        ? isBackofficeFetching
        : isEmployeeFetching;

    useEffect(() => {
        setLoading(isFetching);
    }, [isFetching, setLoading]);

    // Re-sync on every open (not just on data change): React Query serves the same
    // cached reference when reopening the same user, so keying only on userData would
    // leave a prior unsaved edit — and its warning banner — lingering on reopen.
    useEffect(() => {
        if (!props.open) {
            setConfirmOpen(false);
            return;
        }
        const responseUser = userData?.data as User | undefined;
        if (!responseUser) return;
        setUser(responseUser);
        setFirstName(responseUser.firstName);
        setLastName(responseUser.lastName);
        setPhoneNumber(responseUser.phoneNumber);
        setEmail(responseUser.email);
    }, [props.open, userData]);

    // Trim-only (case-sensitive): a case-only edit is still a change we must warn on,
    // since it's the raw value that gets submitted. Pure surrounding whitespace is
    // ignored — the backend normalizes that away.
    const emailChanged = !!user && email.trim() !== user.email.trim();
    // A change here rewrites the shared login for every employment in the set.
    const isSharedEmailChange = emailChanged && !!shared?.isShared;

    // Guard the race: while an email change is pending and the shared-email lookup
    // hasn't resolved, we can't yet know whether to warn — block Save so the change
    // can never slip through unwarned. An errored lookup fails open (advisory only).
    const isSharedLookupPending =
        emailChanged &&
        !props.isAdmin &&
        queryEnabled &&
        !sharedData &&
        !isSharedError;

    const buildRequest = (): UpdateUserRequest => ({
        firstName: firstName,
        lastName: lastName,
        phoneNumber: phoneNumber,
        email: email,
        id: props.userId!
    });

    const onSave = () => {
        if (!props.userId) return;
        if (isSharedEmailChange) {
            setConfirmOpen(true);
            return;
        }
        props.onSubmit(buildRequest());
    };

    const onConfirm = () => {
        setConfirmOpen(false);
        props.onSubmit(buildRequest());
    };

    return (
        <StyledDialog open={props.open} onClose={props.onClose}>
            <StyledDialogContainer style={{ width: '400px' }}>
                <StyledDialogTitle>
                    <SemiBold>{t('edit_employee')}</SemiBold>
                    <SMRegular m={1}>
                        {user?.firstName} {user?.lastName}
                    </SMRegular>
                </StyledDialogTitle>

                <div>
                    <InputText
                        value={firstName}
                        label="First name"
                        name="first_name"
                        error={props.validator.hasError('firstName')}
                        helperText={props.validator.getError('firstName')}
                        onChange={(e) => {
                            setFirstName(e.currentTarget.value);
                            props.setValidator(
                                props.validator.clearError('firstName')
                            );
                        }}
                        spacing="10px 0"
                    />

                    <InputText
                        name="last_name"
                        label="Last name"
                        value={lastName}
                        error={props.validator.hasError('lastName')}
                        helperText={props.validator.getError('lastName')}
                        onChange={(e) => {
                            setLastName(e.currentTarget.value);
                            props.setValidator(
                                props.validator.clearError('lastName')
                            );
                        }}
                        spacing="10px 0"
                    />
                    <MobileInput
                        name="phone_number"
                        label="Phone number"
                        placeholder="+XX XXXXXX"
                        error={props.validator.hasError('phoneNumber')}
                        helperText={
                            props.validator.getError(
                                'phoneNumber',
                                'Phone number including country code'
                            )!
                        }
                        value={phoneNumber}
                        onChange={(e) => {
                            setPhoneNumber(e);
                            props.setValidator(
                                props.validator.clearError('phoneNumber')
                            );
                        }}
                    />

                    {isSharedEmailChange && (
                        <Box mt={1.25}>
                            <Alert
                                severity="warning"
                                sx={{ marginBottom: 0, maxHeight: 'none' }}
                                message={
                                    <span>
                                        <SemiBold display="inline">
                                            {t('shared_email_banner_lead', {
                                                count: shared!.employments
                                                    .length
                                            })}
                                        </SemiBold>{' '}
                                        {t('shared_email_banner_detail')}
                                    </span>
                                }
                            />
                        </Box>
                    )}

                    <InputText
                        name="email"
                        label="Email"
                        type="email"
                        value={email}
                        error={props.validator.hasError('email')}
                        helperText={props.validator.getError('email')}
                        onChange={(e) => {
                            setEmail(e.currentTarget.value);
                            props.setValidator(
                                props.validator.clearError('email')
                            );
                        }}
                        spacing="10px 0"
                    />

                    <Box mt={1} mb={1} textAlign={'center'}>
                        <SMRegular
                            color="secondary"
                            style={{ fontWeight: 'bold' }}
                            display={'inline'}
                        >
                            Note:{' '}
                        </SMRegular>
                        <SMRegular color="secondary" display={'inline'}>
                            The changes you do here may be overwritten during
                            import of users from the salary provider. Please fix
                            the data in the salary provider system if needed.
                        </SMRegular>
                    </Box>
                </div>

                <Box
                    gap={1}
                    mt={2}
                    display={'flex'}
                    justifyContent={'space-between'}
                >
                    <SecondaryButtonStyle onClick={props.onClose}>
                        {t('cancel')}
                    </SecondaryButtonStyle>

                    <PrimaryButtonStyle
                        onClick={onSave}
                        size="medium"
                        disabled={isSharedLookupPending}
                    >
                        {t('save')}
                    </PrimaryButtonStyle>
                </Box>
            </StyledDialogContainer>

            {shared && (
                <EmailChangeWarningDialog
                    open={confirmOpen}
                    employments={shared.employments}
                    oldEmail={user?.email ?? ''}
                    newEmail={email}
                    hasEmploymentsInOtherCompanies={
                        shared.hasEmploymentsInOtherCompanies
                    }
                    onCancel={() => setConfirmOpen(false)}
                    onConfirm={onConfirm}
                />
            )}
        </StyledDialog>
    );
};
